import { BellIcon, ChevronDownIcon } from "lucide-react";
import React from "react";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "../../components/ui/avatar";
import { Card, CardContent } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Header } from "../../components/Header/Header";
import { ComponentHeader } from "../../components/Header/ComponentHeader";
import { useNavigate, useLocation } from "react-router-dom";

// Data for social media links
const SOCIAL_MEDIA_LINKS_BY_COUNTRY: Record<string, Array<{ name: string; icon: string; width: string; height: string; url: string }>> = {
  in: [
    {
        name: "Website",
        icon: "https://c.animaapp.com/m9tvioq8p6jXC2/img/subtract.svg",
        width: "30px",
        height: "30px",
        url: "https://www.weedmanagement.in/"
    },
    {
        name: "Youtube",
        icon: "https://c.animaapp.com/m9tvioq8p6jXC2/img/youtube---png-0.png",
        width: "32px",
        height: "32px",
        url: "https://www.youtube.com/@SyngentaIndia"
    },
    {
        name: "Facebook",
        icon: "https://c.animaapp.com/m9tvioq8p6jXC2/img/facebook---png-0.png",
        width: "32px",
        height: "32px",
        url: "https://www.facebook.com/IndiaSyngenta"
    },
    {
        name: "Instagram",
        icon: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png",
        width: "32px",
        height: "32px",
        url: "https://www.instagram.com/syngentaindia1/"
    },
  ]
  // Add more countries here as needed
};

export const AboutUs = (): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
    const country = countryMatch ? countryMatch[1] : "in";
    const socialMediaLinks: Array<{ name: string; icon: string; width: string; height: string; url: string }> = SOCIAL_MEDIA_LINKS_BY_COUNTRY[country] || SOCIAL_MEDIA_LINKS_BY_COUNTRY['in'];

  const handleGoBack = () => {
        navigate(`/${country}/my-predictions`);
  };
    return (

        <div>
            <Header className="hidden sm:flex"/>
            <ComponentHeader name="About Us" handleClick={handleGoBack}/>
            <div className="bg-[#fcfcfc] flex flex-row justify-center w-full" data-testid="main-container">
                <div className="bg-[#fcfcfc] w-full relative">
                    {/* Sidebar */}
                    <div className="fixed left-0 top-[60px] hidden flex-col w-[100px] h-screen items-start gap-5 p-2.5 bg-primary-new500 sm:flex">
                        <div 
                            className="cursor-pointer relative w-20 h-20 bg-[#014224] rounded-[10px] flex items-center justify-center" 
                            onClick={handleGoBack}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleGoBack();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="icon prediction"
                        >
                            <img
                                className="w-11 h-11"
                                alt="Icon prediction"
                                src="https://c.animaapp.com/m9qofkimNTaxBx/img/icon-prediction.svg"
                            />
                        </div>
                    </div>
                    {/* Main content */}
                    <div className="sm:ml-[100px] w-full sm:w-[calc(100%-100px)]">
                        <div className="h-[calc(100dvh-60px)] overflow-auto">
                            <Card className="flex flex-col items-start gap-4 bg-white rounded-[10px] shadow-[0px_0px_10px_2px_#0000001a]">
                                <div className="flex h-[700px] items-start gap-4 relative self-stretch w-full">
                                    <CardContent className="flex flex-col items-start gap-2.5 p-6 relative flex-1 self-stretch bg-[#fcfcfc] rounded-[10px]">
                                        {/* Content sections */}
                                        <div className="flex flex-col items-start gap-2.5 py-4 pr-3.5 relative self-stretch w-full bg-[#fcfcfc] rounded-[10px]">
                                            <div className="flex flex-col items-end gap-6 relative self-stretch w-full">
                                                <div className="relative self-stretch w-full">
                                                    

                                                    {/* Social media section */}
                                                    <div className="flex flex-col w-full items-start gap-3 mb-8" data-testid="social-media-container">
                                                        {/* Desktop-only About Us heading */}
                                                        <h1 className="hidden sm:block w-full font-bold text-2xl text-black mb-4">
                                                            About Us
                                                        </h1>
                                                         <h2 className="w-[343px] font-bold text-black text-lg leading-tight tracking-wide">
                                                            Connect via social media
                                                        </h2>

                                                        <div className="flex items-start gap-[21px]">
                                                            {socialMediaLinks.map((link: { name: string; icon: string; width: string; height: string; url: string }, index: number) => (
                                                                country === 'in' ? (
                                                                <a
                                                                    key={index}
                                                                    href={link.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="flex flex-col items-center gap-0.5"
                                                                >
                                                                    <div className="flex w-8 h-8 items-center justify-center">
                                                                        <img
                                                                            className="w-auto h-auto"
                                                                            style={{
                                                                                width: link.width,
                                                                                height: link.height,
                                                                            }}
                                                                            alt={link.name}
                                                                            src={link.icon}
                                                                        />
                                                                    </div>
                                                                    <div className="w-14 h-5 font-medium-caption text-neutral-grayneutral-40 text-[var(--medium-caption-font-size)] text-center leading-[var(--medium-caption-line-height)] whitespace-nowrap tracking-[var(--medium-caption-letter-spacing)]">
                                                                        {link.name}
                                                                    </div>
                                                                </a>
                                                                ) : (
                                                                    <div
                                                                        key={index}
                                                                        className="flex flex-col items-center gap-0.5 opacity-50 cursor-not-allowed"
                                                                    >
                                                                        <div className="flex w-8 h-8 items-center justify-center">
                                                                            <img
                                                                                className="w-auto h-auto"
                                                                                style={{
                                                                                    width: link.width,
                                                                                    height: link.height,
                                                                                }}
                                                                                alt={link.name}
                                                                                src={link.icon}
                                                                            />
                                                                        </div>
                                                                        <div className="w-14 h-5 font-medium-caption text-neutral-grayneutral-40 text-[var(--medium-caption-font-size)] text-center leading-[var(--medium-caption-line-height)] whitespace-nowrap tracking-[var(--medium-caption-letter-spacing)]">
                                                                            {link.name}
                                                                        </div>
                                                                    </div>
                                                                )
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};